import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

/**
 * Firebase web config is public by design, but it is still per-environment and
 * selected at build time — never hardcoded to one project (REQUIREMENTS §1.3).
 */
const firebaseConfig: FirebaseOptions = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/**
 * Fail loudly on a bundle built without configuration.
 *
 * Vite inlines a missing VITE_* variable as `undefined`, so a build made in the
 * wrong mode produces an app that looks fine until the first Firebase call,
 * which then fails with an error that says nothing about the real cause. This
 * turns that into one clear message at startup.
 */
const missingConfig = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missingConfig.length > 0) {
  throw new Error(
    `Firebase config is missing: ${missingConfig.join(', ')}. This bundle was ` +
      `built without the VITE_FIREBASE_* variables for its target environment. ` +
      `For a local emulator build use \`vite build --mode emulator\`; for a ` +
      `deployed environment CI must supply them at build time.`,
  );
}

const useEmulators = (import.meta.env.VITE_USE_EMULATORS ?? String(import.meta.env.DEV)) === 'true';

/**
 * THE GUARD (REQUIREMENTS §0.3).
 *
 * Emulator mode is only ever allowed against a demo project id. Firebase treats
 * `demo-*` ids as local-only and refuses to reach any real backend with one, so
 * this check makes an accidental write to `unit-app-dev` or `unit-app-prod`
 * during development structurally impossible rather than merely unlikely.
 */
if (useEmulators && !firebaseConfig.projectId?.startsWith('demo-')) {
  throw new Error(
    `Refusing to start: emulator mode is on but VITE_FIREBASE_PROJECT_ID is ` +
      `"${firebaseConfig.projectId}", which is not a demo project. Use ` +
      `demo-unit-app for local development, or set VITE_USE_EMULATORS=false ` +
      `if you really mean to talk to a cloud project.`,
  );
}

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const storage = getStorage(app);

if (useEmulators) {
  const host = import.meta.env.VITE_EMULATOR_HOST ?? '127.0.0.1';
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(firestore, host, 8080);
  connectStorageEmulator(storage, host, 9199);
} else if (import.meta.env.DEV) {
  console.warn(
    '[firebase] Emulators are OFF in a dev build — this session talks to a real ' +
      `Firebase project (${firebaseConfig.projectId}). Writes are real.`,
  );
}

export const usingEmulators = useEmulators;
